import React from 'react';
import { FormControlLabel, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { GroupPricing, ListingVisibility } from '../../../services/marketplaceService';
import { groupPricingOptionsFor, SellerVisibilityKey, visibilityMeta } from '../../../pages/marketplaceVisibility';
import { VisibilityBadge } from './SellerBadges';
import '../marketplace-design.css';

export interface VisibilityChoiceProps {
    visibility: ListingVisibility;
    onVisibilityChange: (value: ListingVisibility) => void;
    groupPricing: GroupPricing;
    onGroupPricingChange: (value: GroupPricing) => void;
    priceGroup: string;
    onPriceGroupChange: (value: string) => void;
    /** Mandant gehört einer Unternehmensgruppe an (sonst gibt es keine Gruppen-Optionen) */
    hasGroup: boolean;
    groupMembers: Array<{ id: number; name: string | null }>;
    groupTotal: number;
    platformFeePercent: string;
    disabled?: boolean;
}

const MAX_NAMES = 5;

/**
 * Schritt „Wo anbieten?" (Plan 3.4/3.7). Kein reiner Toggle: zwei
 * Auswahlkarten mit eigenem Badge, eigener Akzentfarbe und ausformuliertem
 * Hinweistext. Mandanten ohne Unternehmensgruppe sehen die Gruppen-Optionen
 * gar nicht (statt deaktiviert) — nur die statische Karte „Öffentlich".
 */
const VisibilityChoice: React.FC<VisibilityChoiceProps> = ({
    visibility,
    onVisibilityChange,
    groupPricing,
    onGroupPricingChange,
    priceGroup,
    onPriceGroupChange,
    hasGroup,
    groupMembers,
    groupTotal,
    platformFeePercent,
    disabled = false,
}) => {
    const translate = useTranslate();

    const names = groupMembers.map((m) => m.name ?? `#${m.id}`);
    const shown = names.slice(0, MAX_NAMES).join(', ');
    const more = Math.max(0, groupTotal - Math.min(names.length, MAX_NAMES));

    const hintFor = (key: SellerVisibilityKey): string =>
        key === 'public'
            ? translate(visibilityMeta('public').hintKey, { fee: platformFeePercent })
            : translate(visibilityMeta('group').hintKey, {
                  count: groupTotal,
                  names: more > 0 ? `${shown} ${translate('marketplaceSeller.visibility.group.more', { count: more })}` : shown,
              });

    const card = (key: SellerVisibilityKey, interactive: boolean) => {
        const meta = visibilityMeta(key);
        const selected = visibility === key;
        return (
            <label
                key={key}
                data-testid={`visibility-card-${key}`}
                className={`mk-optc${interactive ? ' clickable' : ''}${selected && interactive ? ' on' : ''}`}
                style={{ marginBottom: 0, borderLeft: `6px solid ${meta.accentVar}`, cursor: interactive && !disabled ? 'pointer' : 'default' }}
            >
                {interactive && (
                    <Radio
                        value={key}
                        disabled={disabled}
                        inputProps={{ 'aria-label': translate(meta.labelKey) }}
                        sx={{ p: 0, mt: '2px' }}
                    />
                )}
                <div className="ot">
                    <div style={{ marginBottom: 6 }}>
                        <VisibilityBadge visibility={key} large />
                    </div>
                    <p style={{ color: 'var(--oa-text)', fontSize: '.88rem' }}>{hintFor(key)}</p>
                </div>
            </label>
        );
    };

    const pricingOptions = groupPricingOptionsFor(visibility);
    const showGroupPricing = hasGroup;

    return (
        <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 12px' }}>{translate('marketplaceSeller.visibility.title')}</h3>

            {hasGroup ? (
                <RadioGroup
                    value={visibility}
                    onChange={(e) => onVisibilityChange(e.target.value as ListingVisibility)}
                    aria-label={translate('marketplaceSeller.visibility.title')}
                    sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' } }}
                >
                    {card('public', true)}
                    {card('group', true)}
                </RadioGroup>
            ) : (
                card('public', false)
            )}

            {showGroupPricing && (
                <div data-testid="group-pricing-block" className="mk-gp">
                    <h4>{translate('marketplaceSeller.groupPricing.title')}</h4>
                    <p className="mk-hint" style={{ marginTop: 2, marginBottom: 8 }}>
                        {translate(
                            visibility === 'group'
                                ? 'marketplaceSeller.groupPricing.introGroup'
                                : 'marketplaceSeller.groupPricing.introPublic',
                        )}
                    </p>
                    <RadioGroup
                        value={groupPricing}
                        onChange={(e) => onGroupPricingChange(e.target.value as GroupPricing)}
                        aria-label={translate('marketplaceSeller.groupPricing.title')}
                    >
                        {pricingOptions.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                disabled={disabled}
                                control={<Radio />}
                                label={translate(`marketplaceSeller.groupPricing.${option}`)}
                            />
                        ))}
                    </RadioGroup>
                    {groupPricing === 'internal' && (
                        <TextField
                            label={translate('marketplaceSeller.groupPricing.priceGroup')}
                            type="number"
                            value={priceGroup}
                            onChange={(e) => onPriceGroupChange(e.target.value)}
                            disabled={disabled}
                            size="small"
                            sx={{ mt: 1, maxWidth: 260 }}
                            slotProps={{
                                htmlInput: { min: 0, step: '0.01' },
                                input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
                            }}
                            helperText={translate('marketplaceSeller.groupPricing.priceGroupHelp')}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default VisibilityChoice;
